import React, { useState } from 'react';
import { getImageUrl } from '@/lib/utils/image';

export interface UFOCardMetadata {
  documentType: string;
  type: string;
  era: string;
  classification: string;
  sourceQuality: string;
  language: string;
  playerName: string;
  siteType?: string;
  entityType?: string;
  mediaType?: string;
  artifactType?: string;
}

export interface UFOCardData {
  title?: string;
  description?: string;
  imageUrl?: string;
  metadata: UFOCardMetadata;
}

interface UFOCardProps {
  cardData: UFOCardData;
}

export const UFOCard: React.FC<UFOCardProps> = ({ cardData }) => {
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Image load error:', e);
    setImageError(true);
    e.currentTarget.onerror = null;
  };

  const imageUrl = getImageUrl(cardData.imageUrl);
  const timestamp = new Date().toLocaleString();

//   console.log('UFOLayout cardData:', cardData);

  return (
    <div className="w-80 min-h-[500px] relative overflow-hidden bg-zinc-100 p-6 font-mono text-sm shadow-md text-black">
      {/* Document texture overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#0001_1px,transparent_1px)] 
        [background-size:4px_4px] opacity-10" />

      {/* Header */}
      <div className="text-center space-y-2 relative">
        <div className="text-xs tracking-wide text-red-600">
          {cardData.metadata.classification.toUpperCase()}
        </div>
        <div className="text-xl font-bold tracking-wider">
          {cardData.title}
        </div>
        <div className="text-xs">
          Document Type: {cardData.metadata.documentType}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t-2 border-black my-4" />

      {/* Document Info */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>Era: {cardData.metadata.era}</div>
        <div>Quality: {cardData.metadata.sourceQuality}</div>
        <div>Agent: {cardData.metadata.playerName}</div>
        <div>Lang: {cardData.metadata.language.toUpperCase()}</div>
      </div>

      {/* Evidence Photo */}
      <div className="mt-4 p-2 bg-black/5 border border-zinc-400">
        <div className="font-bold mb-2">DOCUMENTED EVIDENCE:</div>
        <div className="relative">
          {imageError ? (
            <div className="w-full h-40 bg-zinc-200 flex items-center justify-center border border-zinc-400">
              <span className="text-zinc-600">Image unavailable</span>
            </div>
          ) : (
            <img 
              src={imageUrl}
              alt="UFO Evidence"
              className="w-full h-40 object-cover border border-zinc-400"
              onError={handleImageError}
            />
          )}
          <div className="absolute bottom-0 right-0 bg-black/70 text-white text-xs p-1">
            {timestamp}
          </div>
        </div>
      </div>

      {/* Specific Type Information */}
      <div className="mt-4 p-2 bg-black/5 border border-zinc-400">
        <div className="font-bold">CLASSIFICATION DETAILS:</div>
        <div className="grid grid-cols-2 gap-2">
          {cardData.metadata.siteType && (
            <div>Site Type: {cardData.metadata.siteType}</div>
          )}
          {cardData.metadata.entityType && (
            <div>Entity Type: {cardData.metadata.entityType}</div>
          )}
          {cardData.metadata.mediaType && (
            <div>Media Type: {cardData.metadata.mediaType}</div>
          )}
          {cardData.metadata.artifactType && (
            <div>Artifact Type: {cardData.metadata.artifactType}</div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="mt-4 p-2 bg-black/5 border border-zinc-400">
        <div className="font-bold">FINDINGS:</div>
        <div className="text-sm mt-2">{cardData.description}</div>
      </div>

      {/* Footer */}
      <div className="mt-4 text-center space-y-2 text-xs">
        <div>CONFIDENTIAL - Official Document</div>
        <div>Unauthorized disclosure is prohibited by law</div>
      </div>

      {/* Document ID */}
      <div className="mt-4 flex flex-col items-center space-y-1">
        <div className="w-48 h-8 bg-[linear-gradient(90deg,black_2px,transparent_2px)] 
          [background-size:4px_100%]" />
        <div className="text-xs">DOC-{cardData.metadata.era}-{Math.floor(Math.random() * 10000)}</div>
      </div>
    </div>
  );
};

export default UFOCard; 